import React, { useState } from 'react';
import { WordCard, ImportantButton, WordInputRow, TextSuggestionPanel, GlobeIcon, BulbIcon, ExploreHelpSections } from 'components';
import './HelpAddWordSection.css';

const Globe = () => <span className="help-icon-inline"><GlobeIcon /></span>;
const Bulb = () => <span className="help-icon-inline"><BulbIcon /></span>;

const DUMMY_DEFS = [
  'relating to or having the quality described; typical of the stated kind',
  'a condition or state marked by the named quality or attribute',
  'the tendency or capacity to produce a particular effect or result',
];

const HelpAddWordSection = ({ onBack, onNavigate }) => {
  const [inputValue, setInputValue] = useState('');
  const [newWord, setNewWord] = useState('');
  const [translationEnabled, setTranslationEnabled] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingDefs, setIsLoadingDefs] = useState(false);
  const [wordAdded, setWordAdded] = useState(null);

  const handleSubmit = () => {
    if (inputValue.trim() === '') return;
    if (newWord) {
      setWordAdded({ word: newWord, definition: inputValue.trim() });
      setInputValue('');
    } else {
      const word = inputValue.trim();
      setNewWord(word);
      setInputValue('');
      if (translationEnabled) {
        setIsLoadingDefs(true);
        setPanelOpen(true);
        setTimeout(() => {
          setSuggestions(DUMMY_DEFS);
          setIsLoadingDefs(false);
        }, 500);
      }
    }
  };

  const reset = () => {
    setInputValue('');
    setNewWord('');
    setTranslationEnabled(true);
    setPanelOpen(false);
    setSuggestions([]);
    setIsLoadingDefs(false);
    setWordAdded(null);
  };

  if (wordAdded) {
    return (
      <div className="help-page">
        <div className="help-finished">
          <p>Word added: <strong>{wordAdded.word}</strong> = {wordAdded.definition}</p>
          <p>That's how you build your card sets!</p>
          <ImportantButton buttonText="Try again" onClick={reset} />
          <ExploreHelpSections currentSection="addWord" onNavigate={onNavigate} />
        </div>
      </div>
    );
  }

  const hint = () => {
    if (!newWord) {
      return (
        <div className="hint-container">
          <p className="help-hint">Type a word and press <strong>Enter (↵)</strong>.</p>
          <p className="help-hint">
            <Globe /> enables the dictionary. When on, AutoLexi fetches
            definitions automatically after you enter a word.
          </p>
          <p className="help-hint">Turn it off to skip suggestions and type your definition directly.</p>
        </div>
      );
    }
    if (panelOpen) {
      return (
        <div className="hint-container">
          <p className="help-hint">
            {isLoadingDefs
              ? 'Looking up definitions…'
              : <>Browse with <strong>▲▼</strong>, edit if needed, and hit <strong>Accept ✓</strong> to copy one into the input.</>}
          </p>
          <p className="help-hint"><Bulb /> hides or shows this panel at any time.</p>
        </div>
      );
    }
    return (
      <div className="hint-container">
        <p className="help-hint">Type a definition and press <strong>Enter (↵)</strong> to add the word.</p>
        {suggestions.length > 0 && (
          <p className="help-hint">Click <Bulb /> to re-open the suggestions panel.</p>
        )}
      </div>
    );
  };

  return (
    <div className="help-add-word-layout">
      <WordCard word={newWord || 'Your word will be added to the card set'} />
      <div className="help-add-word-input-wrap">
        <TextSuggestionPanel
          visible={panelOpen}
          onClose={() => setPanelOpen(false)}
          onAccept={(text) => { setInputValue(text); setPanelOpen(false); }}
          suggestions={suggestions}
          suggestionsLoading={isLoadingDefs}
        />
        <WordInputRow
          value={inputValue}
          callbackOnChange={(e) => setInputValue(e.target.value)}
          callbackOnKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          placeholder={newWord ? 'Add definition' : 'Add word'}
          loading={false}
          translationEnabled={translationEnabled}
          onToggleTranslation={() => setTranslationEnabled((prev) => !prev)}
          suggestionsOpen={panelOpen}
          onToggleSuggestions={newWord ? () => setPanelOpen((prev) => !prev) : undefined}
        />
      </div>
      <div className="help-hint-area">
        {hint()}
        <div style={{ marginTop: 8 }} className={"back-button"}>
          <ImportantButton buttonText="Back" onClick={onBack} />
        </div>
      </div>
    </div>
  );
};

export { HelpAddWordSection };
